import type { ReactNode } from 'react';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import { useChannelsT } from '../useChannelsT';
import { CheckGlyph, delay, tone } from '../../primitives';

/** The agent's install snippet, as Channels → Website shows it. */
export function InstallSnippet({ className }: { className?: string }) {
  return (
    <pre
      dir='ltr'
      className={clsxm(
        'overflow-x-auto rounded-xl bg-[#0f0e20] px-4 py-3 text-left font-mono text-[11.5px] leading-[1.6] text-[#c9c9e3] ring-1 ring-inset ring-white/10',
        className,
      )}
    >
      <code>
        <span className='text-[#7d7da0]'>&lt;</span>
        <span className='text-[#9aa7ff]'>script</span>
        {'\n  '}
        <span className='text-[#7fd6c2]'>src</span>
        <span className='text-[#7d7da0]'>=</span>
        <span className='text-[#f2c98b]'>
          &quot;https://bots.easy-peasy.ai/chat.min.js&quot;
        </span>
        {'\n  '}
        <span className='text-[#7fd6c2]'>data-chat-url</span>
        <span className='text-[#7d7da0]'>=</span>
        <span className='text-[#f2c98b]'>&quot;…/bot/your-agent&quot;</span>
        {'\n  '}
        <span className='text-[#7fd6c2]'>defer</span>
        <span className='text-[#7d7da0]'>&gt;&lt;/</span>
        <span className='text-[#9aa7ff]'>script</span>
        <span className='text-[#7d7da0]'>&gt;</span>
      </code>
    </pre>
  );
}

/**
 * A site builder's own "custom code" screen with the install snippet pasted
 * in, drawn in the builder's colours, and a note that the chat is live.
 */
export default function CodePanel({
  platform,
  title,
  fields,
  codeLabel,
  primary,
  primaryClass,
  note,
}: {
  /** The builder's logo and name, as its window shows them. */
  platform: ReactNode;
  title: string;
  /** Settings shown above the code, as label and value. */
  fields: { label: string; value: ReactNode }[];
  codeLabel: string;
  primary: string;
  /** Tailwind classes for the builder's button colour. */
  primaryClass: string;
  note: string;
}) {
  const { t } = useChannelsT();

  return (
    <div className='relative mx-auto max-w-[31rem] pb-2 sm:pb-14'>
      <div
        className={clsxm(
          'overflow-hidden rounded-[22px] text-start shadow-[0_40px_90px_-35px_rgba(30,20,90,0.55)]',
          tone.surface,
          tone.line,
        )}
      >
        <div className='flex items-center gap-2.5 border-b border-black/[0.06] px-5 py-3 dark:border-white/[0.06]'>
          {platform}
        </div>
        <div className='px-5 py-5'>
          <p className={clsxm('text-[16px] font-semibold', tone.ink)}>
            {title}
          </p>
          {fields.length > 0 && (
            <dl className='mt-4 grid gap-3 sm:grid-cols-2'>
              {fields.map(({ label, value }) => (
                <div key={label}>
                  <dt className={clsxm('text-xs', tone.quiet)}>{label}</dt>
                  <dd
                    className={clsxm(
                      'mt-1 rounded-lg px-3 py-2 text-[13px] font-medium ring-1 ring-inset ring-[#0f0f28]/10 dark:ring-white/10',
                      tone.ink,
                    )}
                  >
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          <p className={clsxm('mt-4 text-xs font-medium', tone.quiet)}>
            {codeLabel}
          </p>
          <InstallSnippet className='mt-1.5' />
          <div className='mt-4 flex justify-end'>
            <span
              className={clsxm(
                'rounded-lg px-4 py-2 text-[13px] font-semibold',
                primaryClass,
              )}
            >
              {primary}
            </span>
          </div>
        </div>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 flex items-center gap-3 rounded-2xl p-3.5 pe-4 text-start shadow-[0_30px_60px_-28px_rgba(30,20,90,0.6)] sm:absolute sm:-bottom-3 sm:start-[-1.5rem] sm:mt-0 sm:w-[17rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(450)}
      >
        <span className='flex h-8 w-8 flex-none items-center justify-center rounded-full bg-emerald-500 text-white'>
          <CheckGlyph className='h-4 w-4' />
        </span>
        <span className='min-w-0'>
          <span className={clsxm('block text-[13px] font-semibold', tone.ink)}>
            {t('Chat is live on every page')}
          </span>
          <span className={clsxm('block text-xs leading-5', tone.quiet)}>
            {note}
          </span>
        </span>
      </div>
    </div>
  );
}
